import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import { withStyles } from '@material-ui/core';

import Transition from '../../components/Dialogs/Transition';
import FMPStationAutoComplete from '../../components/pureForm/FMPStationAutoComplete';
import { search as searchStations } from '../../modules/station';
import { search as searchCarriers } from '../../modules/logistics/carrier';
import {
	add,
	update,
	suggestMatchText,
	textAppearsIn,
} from '../../modules/apAutomation/carrierStationMapping';

const styles = () => ({
	dialogPaper: {
		maxWidth: '100%',
		width: 620,
	},
	dialogPadding: {
		padding: '10px 25px 20px 25px',
	},
	destination: {
		fontFamily: 'monospace',
		fontSize: '0.85rem',
		backgroundColor: '#fafafa',
		border: '1px solid #eee',
		borderRadius: 4,
		padding: '8px 12px',
		marginBottom: 12,
	},
	row: {
		fontSize: '0.9rem',
		marginBottom: 6,
	},
	field: {
		marginTop: 12,
	},
	errorText: {
		color: '#c62828',
		fontSize: '0.85rem',
		marginTop: 8,
	},
});

/**
 * Add or edit a carrier station mapping. Opened from AP triage with the destination of a
 * stop that had no station code (pass carrierId + destination), or from the Station
 * Mappings page (pass mapping to edit, or nothing to add a blank one).
 */
class StationMappingDialog extends React.Component {
	state = {
		carrierId: '',
		matchText: '',
		stationId: 0,
		notes: '',
		isSaving: false,
		error: null,
	};

	componentDidMount() {
		const { stations, carriers, dispatchSearchStations, dispatchSearchCarriers } = this.props;
		if (_.isEmpty(stations)) {
			dispatchSearchStations();
		}
		if (_.isEmpty(carriers)) {
			dispatchSearchCarriers();
		}
	}

	componentDidUpdate(prevProps) {
		//- Reset the form each time the dialog opens
		if (this.props.open && !prevProps.open) {
			const { mapping, carrierId, destination } = this.props;
			if (mapping) {
				this.setState({
					carrierId: mapping.carrierId,
					matchText: mapping.matchText,
					stationId: mapping.stationId,
					notes: mapping.notes || '',
					isSaving: false,
					error: null,
				});
			} else {
				this.setState({
					carrierId: carrierId || '',
					matchText: destination ? suggestMatchText(destination) : '',
					stationId: 0,
					notes: destination ? `Bill destination: ${destination}` : '',
					isSaving: false,
					error: null,
				});
			}
		}
	}

	matchTextProblem = () => {
		const { destination } = this.props;
		const matchText = _.trim(this.state.matchText);
		if (!matchText) {
			return null;
		}
		if (destination && !textAppearsIn({ matchText, destination })) {
			return 'This text does not appear in the destination above, so it will not match this bill.';
		}
		return null;
	};

	handleSave = () => {
		const { mapping, onSaved } = this.props;
		const { carrierId, stationId, notes } = this.state;
		const matchText = _.trim(this.state.matchText);

		if (!carrierId) {
			return this.setState({ error: 'Pick the carrier' });
		}
		if (!matchText) {
			return this.setState({ error: 'Enter the text to look for' });
		}
		if (!stationId) {
			return this.setState({ error: 'Pick the station' });
		}

		this.setState({ isSaving: true, error: null });
		const callback = (err, r) => {
			if (err) {
				const errors = _.isArray(err) ? err : [err];
				return this.setState({
					isSaving: false,
					error: errors.map((e) => (e && e.message) || String(e)).join(', '),
				});
			}
			this.setState({ isSaving: false });
			onSaved(r);
		};
		if (mapping) {
			update({
				carrierStationMappingId: mapping.carrierStationMappingId,
				matchText,
				stationId,
				notes,
				callback,
			});
		} else {
			add({ carrierId: parseInt(carrierId, 10), matchText, stationId, notes, callback });
		}
	};

	render() {
		const {
			classes,
			open,
			onClose,
			mapping,
			destination,
			fuelAmount,
			stations,
			carriers,
		} = this.props;
		const { carrierId, matchText, stationId, notes, isSaving, error } = this.state;
		const isEditing = !!mapping;
		const matchTextProblem = this.matchTextProblem();

		return (
			<Dialog
				classes={{ paper: classes.dialogPaper }}
				open={open}
				TransitionComponent={Transition}
				onClose={isSaving ? undefined : onClose}
			>
				<div className={classes.dialogPadding}>
					<DialogTitle>{isEditing ? 'Edit Station Mapping' : 'Map to Station'}</DialogTitle>
					<DialogContent>
						{destination && (
							<>
								<div className={classes.row}>
									The bill names this stop{fuelAmount ? ` (${fuelAmount} gallons)` : ''}:
								</div>
								<div className={classes.destination}>{destination}</div>
								<div className={classes.row}>
									Pick a part of it that will always be on bills from this carrier for this station,
									usually the street address, and the station it means. The AI sometimes misreads
									street numbers, so a business name or store number (like #90412) can be steadier
									when it is on the bill.
								</div>
							</>
						)}

						<TextField
							select
							id="stationMappingCarrier"
							label="Carrier"
							value={carrierId}
							fullWidth
							className={classes.field}
							disabled={isEditing || !!this.props.carrierId}
							onChange={(e) => this.setState({ carrierId: e.target.value, error: null })}
						>
							{carriers.map((carrier) => (
								<MenuItem key={carrier.carrierId} value={carrier.carrierId}>
									{carrier.name}
								</MenuItem>
							))}
						</TextField>

						<TextField
							id="stationMappingMatchText"
							label="Text to look for"
							value={matchText}
							fullWidth
							autoFocus
							inputProps={{ maxLength: 255 }}
							className={classes.field}
							error={!!matchTextProblem}
							helperText={
								matchTextProblem ||
								'Capitals, punctuation and STREET / ST, EAST / E style spellings do not matter.'
							}
							onChange={(e) => this.setState({ matchText: e.target.value, error: null })}
						/>

						<div className={classes.field}>
							<FMPStationAutoComplete
								label="Station"
								path="stationId"
								value={stationId}
								stations={stations}
								onChange={(value) => this.setState({ stationId: value, error: null })}
							/>
						</div>

						<TextField
							id="stationMappingNotes"
							label="Notes"
							value={notes}
							fullWidth
							multiline
							inputProps={{ maxLength: 1024 }}
							className={classes.field}
							onChange={(e) => this.setState({ notes: e.target.value })}
						/>

						{error && <div className={classes.errorText}>{error}</div>}
					</DialogContent>
					<DialogActions>
						<Button onClick={onClose} disabled={isSaving}>
							Cancel
						</Button>
						<Button
							color="primary"
							variant="contained"
							onClick={this.handleSave}
							disabled={isSaving || !_.trim(matchText) || !stationId}
						>
							{isSaving ? <CircularProgress size={20} /> : isEditing ? 'Save' : 'Add Mapping'}
						</Button>
					</DialogActions>
				</div>
			</Dialog>
		);
	}
}

StationMappingDialog.propTypes = {
	classes: PropTypes.object.isRequired,
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired,
	//- edit an existing mapping
	mapping: PropTypes.object,
	//- add one from a triage stop
	carrierId: PropTypes.number,
	destination: PropTypes.string,
	fuelAmount: PropTypes.number,
	//- redux
	stations: PropTypes.array.isRequired,
	carriers: PropTypes.array.isRequired,
	dispatchSearchStations: PropTypes.func.isRequired,
	dispatchSearchCarriers: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
	stations: _.compact(_.map(state.lists.stationIds, (id) => state.maps.station[id])),
	carriers: _.sortBy(
		_.compact(_.map(state.lists.carrierIds, (id) => state.maps.carrier[id])),
		'name'
	),
});

export default connect(mapStateToProps, {
	dispatchSearchStations: searchStations,
	dispatchSearchCarriers: searchCarriers,
})(withStyles(styles)(StationMappingDialog));
